import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Search, RefreshCw, Download, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PAY_STATUS = ["Pago pendiente", "Pagado", "Cancelado"];
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("es", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; } };

export default function MesaAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [fPay, setFPay] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => { setLoading(true); try { const { data } = await api.get("/mesa-registrations"); setItems(data); } catch (e) { /* ignore */ } setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, changes) => {
    setItems((p) => p.map((r) => r.id === id ? { ...r, ...changes } : r));
    try { await api.patch(`/mesa-registrations/${id}`, changes); } catch (e) { load(); }
  };

  const filtered = useMemo(() => items.filter((r) => {
    const hay = `${r.nombre} ${r.email} ${r.phone} ${r.business_name || ""}`.toLowerCase();
    if (q && !hay.includes(q.toLowerCase())) return false;
    if (fPay !== "all" && r.payment_status !== fPay) return false;
    return true;
  }), [items, q, fPay]);

  const cards = useMemo(() => [
    ["Total", items.length],
    ["Pago pendiente", items.filter((r) => r.payment_status === "Pago pendiente").length],
    ["Pagado", items.filter((r) => r.payment_status === "Pagado").length],
    ["Cancelado", items.filter((r) => r.payment_status === "Cancelado").length],
  ], [items]);

  const exportCSV = () => {
    const rows = [["Fecha", "Nombre", "Email", "Teléfono", "Negocio", "Tipo de negocio", "Apoyo solicitado", "Espera obtener", "Tema para la cena", "Estado de pago", "Notas"]];
    filtered.forEach((r) => rows.push([
      r.created_at, r.nombre, r.email, r.phone, r.business_name || "", r.business_type || "",
      (r.support_needs || []).join("; ") + (r.support_needs_other ? ` (${r.support_needs_other})` : ""),
      r.expectations || "", r.dinner_topic || "", r.payment_status, r.admin_notes || "",
    ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)));
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `mesa_emprendedora_${Date.now()}.csv`; a.click();
  };

  const payBadge = (s) => s === "Pagado" ? "bg-green-100 text-green-700" : s === "Cancelado" ? "bg-red-100 text-red-600" : "bg-gold/30 text-navy";

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="mesa-summary">
        {cards.map(([label, v]) => (
          <div key={label} className="bg-white rounded-2xl border border-navy/5 p-4 text-center">
            <div className="text-2xl font-extrabold text-brand">{v}</div>
            <div className="text-[11px] uppercase tracking-wide text-ink/50 mt-1 leading-tight">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input data-testid="mesa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, email, teléfono, negocio" className="w-full bg-white border border-navy/10 rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <select value={fPay} onChange={(e) => setFPay(e.target.value)} data-testid="mesa-filter-pay" className="bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm"><option value="all">Todos los pagos</option>{PAY_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <button onClick={exportCSV} data-testid="mesa-export" className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-4 py-2.5 text-sm font-semibold"><Download size={15} /> CSV</button>
        <button onClick={load} data-testid="mesa-refresh" className="inline-flex items-center gap-2 bg-white border border-navy/10 rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-aqua"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /></button>
      </div>

      {filtered.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border text-ink/50">No hay registros todavía.</div> :
        <div className="space-y-4" data-testid="mesa-list">
          {filtered.map((r) => {
            const open = expanded === r.id;
            return (
              <div key={r.id} className="bg-white rounded-3xl border border-navy/5 overflow-hidden" data-testid={`mesa-row-${r.id}`}>
                <div className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-heading font-bold text-navy">{r.nombre}</span>
                        <span className={`text-xs font-bold rounded-full px-3 py-1 ${payBadge(r.payment_status)}`} data-testid={`mesa-pay-badge-${r.id}`}>{r.payment_status}</span>
                        <span className="text-xs text-ink/40">{fmt(r.created_at)}</span>
                      </div>
                      <div className="text-sm text-ink/60 mb-1">{r.business_name || "—"}{r.business_type ? ` · ${r.business_type}` : ""}</div>
                      <div className="flex flex-wrap gap-x-4 text-sm"><a href={`mailto:${r.email}`} className="text-brand hover:underline">{r.email}</a><a href={`tel:${r.phone}`} className="text-brand hover:underline">{r.phone}</a></div>
                    </div>
                    <div className="w-full sm:w-56">
                      <Select value={r.payment_status} onValueChange={(v) => patch(r.id, { payment_status: v })}><SelectTrigger data-testid={`mesa-pay-${r.id}`} className="rounded-xl bg-white text-xs h-9"><SelectValue /></SelectTrigger><SelectContent>{PAY_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                    </div>
                  </div>
                  <button onClick={() => setExpanded(open ? null : r.id)} data-testid={`mesa-open-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold bg-offwhite border border-navy/10 rounded-full px-3 py-1.5 mt-3">{open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {open ? "Ocultar" : "Ver respuestas"}</button>
                </div>
                {open && (
                  <div className="border-t border-navy/5 bg-offwhite/50 p-5 space-y-2 text-sm">
                    <div><span className="font-semibold text-navy">Apoyo solicitado: </span><span className="text-ink/70">{(r.support_needs || []).join(", ") || "—"}{r.support_needs_other ? ` (${r.support_needs_other})` : ""}</span></div>
                    <div><span className="font-semibold text-navy">Qué espera obtener: </span><span className="text-ink/70">{r.expectations || "—"}</span></div>
                    <div><span className="font-semibold text-navy">Tema para la cena: </span><span className="text-ink/70">{r.dinner_topic || "—"}</span></div>
                    <div><label className="text-xs font-semibold text-navy">Notas internas</label><textarea defaultValue={r.admin_notes || ""} onBlur={(e) => patch(r.id, { admin_notes: e.target.value })} rows={2} className="w-full mt-1 bg-white border border-navy/10 rounded-xl px-3 py-2 text-sm" data-testid={`mesa-notes-${r.id}`} /></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>}
    </div>
  );
}
